import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-brand">
        <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-8 w-8 text-brand-accent" />
            <span className="font-display font-black text-2xl tracking-tight">CMMC Shield</span>
          </div>
          <div>
            <h2 className="font-display font-bold text-3xl leading-tight mb-3">Audit-ready against all 110 NIST 800-171 controls.</h2>
            <p className="text-slate-300 max-w-md">Automated gap analysis, instant policy generation, and continuous evidence — built for defense contractors.</p>
          </div>
          <div className="text-xs text-slate-500 font-mono">DFARS 252.204-7012 / -7019 / -7020 / -7021</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="font-display font-bold text-2xl tracking-tight mb-1">Sign in</h1>
          <p className="text-slate-500 text-sm mb-6">Access your compliance command center.</p>
          {error && <div className="mb-4 text-sm text-danger bg-danger-bg border border-red-200 rounded px-3 py-2" data-testid="login-error">{error}</div>}
          <label className="text-sm font-medium text-slate-700">Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="login-email"
            className="w-full mt-1 mb-4 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-accent" />
          <label className="text-sm font-medium text-slate-700">Password</label>
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required data-testid="login-password"
            className="w-full mt-1 mb-6 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-accent" />
          <button disabled={loading} data-testid="login-submit"
            className="w-full bg-brand text-white rounded-md py-2.5 font-medium hover:bg-brand-secondary transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
          <p className="text-sm text-slate-500 mt-6 text-center">
            No account? <Link to="/register" className="text-brand-accent font-medium" data-testid="to-register">Start free</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
